// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React, { useEffect } from 'react';
import {hot} from 'react-hot-loader/root';
import {Provider} from 'react-redux';
import {Router, Route} from 'react-router-dom';
import ReactGA from 'react-ga4'; // GA4 is the new version of Google Analytics

import store from 'stores/redux_store';

import {makeAsyncComponent} from 'components/async_load';
import CRTPostsChannelResetWatcher from 'components/threading/channel_threads/posts_channel_reset_watcher';

import {getHistory} from 'utils/browser_history';
const LazyRoot = React.lazy(() => import('components/root'));

const Root = makeAsyncComponent('Root', LazyRoot);

const getGAID = (): string => {
    const domain = window.location.hostname;
    const gaIDs: { [key: string]: string } = {
        'chat.uclone.net': 'G-0H3JY90L0W',
        'stg.uclone.net': 'G-TJFDNLE59T',
    };
    return gaIDs[domain] || 'G-1QXP91B3HZ';
};

const App = () => {
    const history = getHistory();

    useEffect(() => {
        // GA4 initialize
        const gaID = getGAID();
        if (gaID) { // Ensure we have an ID before initializing
            ReactGA.initialize(gaID);

            // Send initial page view
            ReactGA.send({ hitType: 'pageview', page: window.location.pathname, title: document.title });

            // Send detailed initial load timing metrics
            if (performance.getEntriesByType) { // Check if the API is available
                const navigationTiming = performance.getEntriesByType("navigation");
                if (navigationTiming.length > 0) {
                    const navEntry = navigationTiming[0] as PerformanceNavigationTiming; // Type assertion
                    ReactGA.event("initial_load_metrics", {
                        page_path: window.location.pathname,
                        load_duration_ms: Math.round(navEntry.duration),
                        dom_interactive_ms: Math.round(navEntry.domInteractive),
                        dom_content_loaded_ms: Math.round(navEntry.domContentLoadedEventEnd),
                        load_event_end_ms: Math.round(navEntry.loadEventEnd),
                        event_category: "Performance", // Optional: for easier filtering if also using UA or for consistency
                        event_label: "Initial Page Load" // Optional
                    });
                }
            } else {
                // Fallback for initial load time if navigation timing API is not fully supported or already passed
                // This is similar to the root_load event we had in root.html
                ReactGA.event("initial_load_simple", {
                    page_path: window.location.pathname,
                    load_time_ms: Math.round(performance.now()),
                    event_category: "Performance",
                    event_label: "Initial Load (performance.now)"
                });
            }
        }

        const unlisten = history.listen((location, action) => {
            if (gaID) { // Ensure GA is initialized
                // Send SPA page view
                ReactGA.send({ hitType: 'pageview', page: location.pathname, title: document.title });

                // Send SPA route load timing (time from initial load to this route change)
                ReactGA.event("spa_route_load", {
                    page_path: location.pathname,
                    load_time_ms: Math.round(performance.now()), // Time since initial navigation
                    event_category: "Performance",
                    event_label: "SPA Route Load"
                });
            }
        });
        return () => unlisten();
    }, [history]);

    return (
        <Provider store={store}>
            <CRTPostsChannelResetWatcher/>
            <Router history={getHistory()}>
                <Route
                    path='/'
                    component={Root}
                />
            </Router>
        </Provider>
    );
};

export default hot(React.memo(App));
